import { useState, useEffect, useRef, RefObject, Dispatch, SetStateAction } from "react";

interface UseInfiniteScrollReturnType {
  page: number;
  isScrollEnabled: boolean;
  setIsScrollEnabled: Dispatch<SetStateAction<boolean>>;
  searchPage: number;
  setIsSearchScrollEnabled: Dispatch<SetStateAction<boolean>>;
  isSearchScrollEnabled: boolean;
  isSearchScrollLoading: boolean;
  setIsSearchScrollLoading: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  setSearchPage: Dispatch<SetStateAction<number>>;
  scrollContainerRef: RefObject<HTMLDivElement>;
}

const useInfiniteScroll = (): UseInfiniteScrollReturnType => {
  const [page, setPage] = useState<number>(0);
  const [isScrollEnabled, setIsScrollEnabled] = useState<boolean>(false);
  const [searchPage, setSearchPage] = useState<number>(0);
  const [isSearchScrollEnabled, setIsSearchScrollEnabled] = useState<boolean>(false);
  const [isSearchScrollLoading, setIsSearchScrollLoading] = useState<boolean>(false);
  const [isEndReached, setIsEndReached] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadThreshold = 0.8;

  useEffect(() => {
    const handleScroll = () => {
      if (!isScrollEnabled || isSearchScrollLoading) return;

      if (scrollContainerRef.current) {
        const { scrollTop, clientHeight, scrollHeight } = scrollContainerRef.current;
        const scrollPosition = scrollTop + clientHeight;
        const thresholdHeight = scrollHeight * loadThreshold;
        if (scrollPosition >= thresholdHeight && !isEndReached) {
          setIsEndReached(true);
        } else {
          setIsEndReached(false);
        }
      }
    };

    const ref = scrollContainerRef.current;
    if (ref) {
      ref.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (ref) {
        ref.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isScrollEnabled, isSearchScrollLoading, isEndReached]);

  useEffect(() => {
    if (isEndReached) {
      setPage((prevPage) => prevPage + 1);
      setIsEndReached(false);
    }
  }, [isEndReached]);

  return {
    page,
    isScrollEnabled,
    setIsScrollEnabled,
    searchPage,
    setIsSearchScrollEnabled,
    isSearchScrollEnabled,
    isSearchScrollLoading,
    setIsSearchScrollLoading,
    setPage,
    setSearchPage,
    scrollContainerRef,
  };
};

export default useInfiniteScroll;
